import React, { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { api } from "@/lib/api";
import { resyncPush, unlinkPush } from "@/lib/push";
import type { User, Role } from "@/types";

interface AuthCtx {
  user: User | null | false;
  setUser: (u: User | false) => void;
  refreshMe: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthCtx | null>(null);
const CACHE = "wn_user";

export const homeFor = (role: Role) => (role === "customer" ? "/app" : role === "venue" ? "/venue" : "/admin");

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null | false>(null);

  const setUser = (u: User | false) => {
    setUserState(u);
    if (u) {
      localStorage.setItem(CACHE, JSON.stringify(u));
      resyncPush().catch(() => {});
    }
    else localStorage.removeItem(CACHE);
  };

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e: any) {
      const cached = localStorage.getItem(CACHE);
      if (!e?.response && cached) setUserState(JSON.parse(cached));
      else setUser(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const logout = async () => {
    try {
      await unlinkPush();
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  return <AuthContext.Provider value={{ user, setUser, refreshMe, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext) as AuthCtx;
